import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { RouteProgressBar } from "@/components/route-progress-bar";
import { trackPageview } from "@/lib/analytics";
import { initWebVitals } from "@/lib/web-vitals";

import appCss from "../styles.css?url";
import interLatin400 from "@fontsource/inter/files/inter-latin-400-normal.woff2?url";
import interLatin600 from "@fontsource/inter/files/inter-latin-600-normal.woff2?url";
import spaceGroteskLatin700 from "@fontsource/space-grotesk/files/space-grotesk-latin-700-normal.woff2?url";

function NotFoundComponent() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "404 — Strona nie znaleziona | Tanie Latanie";
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,follow");
    return () => {
      document.title = prevTitle;
      if (created) meta?.remove();
      else if (prevContent != null) meta?.setAttribute("content", prevContent);
    };
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Nie znaleziono strony</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Strona, której szukasz, nie istnieje lub została przeniesiona.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Wróć na stronę główną
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#0b0b0f", media: "(prefers-color-scheme: dark)" },
      { name: "author", content: "Tanie Latanie" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" },
      { property: "og:site_name", content: "Tanie Latanie" },
      { property: "og:locale", content: "pl_PL" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "format-detection", content: "telephone=no" },
      // iOS standalone (Add to Home Screen) — pełnoekranowe PWA bez paska Safari.
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Tanie Latanie" },
      { name: "mobile-web-app-capable", content: "yes" },
    ],

    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "preconnect", href: "https://gxtcvarcxdxgyxvntuqx.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://gxtcvarcxdxgyxvntuqx.supabase.co" },
      { rel: "dns-prefetch", href: "https://www.esky.pl" },
      // Fonty są self-hostowane przez @fontsource (font-display: swap wbudowany).
      // Preload krytycznych wariantów (latin) używanych above-the-fold w hero/headerze —
      // skraca FOUT i poprawia LCP/CLS w Google PageSpeed.
      { rel: "preload", as: "font", type: "font/woff2", href: spaceGroteskLatin700, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: interLatin400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: interLatin600, crossOrigin: "anonymous" },
      { rel: "me", href: "https://tanielatanie.pl/" },
      { rel: "alternate", type: "application/rss+xml", title: "Tanie Latanie — Blog (RSS)", href: "https://tanielatanie.pl/rss.xml" },
    ],
    scripts: [
      {
        // Skrypt reklamowy (emrldco) — ładowany asynchronicznie, bez defer.
        type: "text/javascript",
        children: `(function () {
  var script = document.createElement("script");
  script.async = 1;
  script.setAttribute("data-cmp-ab","2");
  script.src = 'https://emrldco.com/NTgwODc3.js?t=580877';
  document.head.appendChild(script);
})();`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": ["Organization", "TravelAgency"],
              "@id": "https://tanielatanie.pl/#org",
              name: "Tanie Latanie",
              url: "https://tanielatanie.pl",
              logo: {
                "@type": "ImageObject",
                url: "https://tanielatanie.pl/apple-touch-icon.png",
                width: 90,
                height: 90,
              },
              areaServed: { "@type": "Country", name: "Poland" },
              knowsLanguage: ["pl", "en"],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer support",
                  url: "https://tanielatanie.pl/contact",
                  availableLanguage: ["pl", "en"],
                  areaServed: "PL",
                },
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://tanielatanie.pl/#website",
              url: "https://tanielatanie.pl",
              name: "Tanie Latanie",
              alternateName: ["Tanielatanie.pl", "tanielatanie"],
              inLanguage: "pl-PL",
              publisher: { "@id": "https://tanielatanie.pl/#org" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://tanielatanie.pl/search?from=WAW&to={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "WebApplication",
              "@id": "https://tanielatanie.pl/#webapp",
              name: "Tanie Latanie",
              url: "https://tanielatanie.pl",
              applicationCategory: "TravelApplication",
              operatingSystem: "Web",
              offers: { "@type": "Offer", price: "0", priceCurrency: "PLN" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent as never,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // Inline-skrypt: ustawia klasę .dark na <html> ZANIM React zacznie renderować,
  // dzięki temu nie ma migotania (FOUC). Czytamy localStorage("theme"); gdy
  // brak — domyślnie "dark" (oryginalna paleta serwisu).
  const themeBootstrap = `(function(){try{var t=localStorage.getItem('theme');var d;if(t==='light'){d=false;}else if(t==='dark'){d=true;}else if(t==='system'){d=window.matchMedia('(prefers-color-scheme: dark)').matches;}else{d=true;}var e=document.documentElement;e.classList.toggle('dark',d);e.style.colorScheme=d?'dark':'light';}catch(e){document.documentElement.classList.add('dark');}})();`;
  return (
    <html lang="pl" dir="ltr" className="dark" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: themeBootstrap }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Logujemy pełny href (pathname + search), żeby w „Live ścieżki" widzieć
  // zmiany filtrów na /search (stops, airline, bag, sort, maxPrice itd.).
  // Każda zmiana query params = nowy event w analytics_events.
  const href = useRouterState({ select: (s) => s.location.href });

  useEffect(() => {
    trackPageview(href).catch(() => {});
  }, [href]);

  useEffect(() => {
    initWebVitals();
    // Przeglądarka nie przywraca starej pozycji przy „wstecz".
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // Każda zmiana podstrony (także powrót) = start od góry.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouteProgressBar />
      <main id="main"><Outlet /></main>
      <Toaster />
    </QueryClientProvider>
  );
}
